import React, { useState } from "react";
import { JsonViewer, CollapsibleSection } from "./SharedDebugComponents";
import './apiDebug.css';

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function getMethodLabel(method) {
  if (!method) return 'Desconocido';
  if (method.includes('deleteFromLinkedToList')) return 'Desincronización (Unlink)';
  if (method.includes('linkProducts')) return 'Sincronización (Link)';
  return method;
}

function getUserDisplay(requestedBy) {
  if (!requestedBy) return 'Sistema / Automático';
  if (typeof requestedBy === 'string') return requestedBy;
  return requestedBy.name || requestedBy.email || requestedBy._id || 'Desconocido';
}

function getProductDisplay(product) {
  if (!product) return 'N/A';
  if (typeof product === 'string') return product;
  const parts = [];
  if (product.name) parts.push(product.name);
  if (product.sku) parts.push(`SKU: ${product.sku}`);
  if (product.odooId) parts.push(`Odoo: ${product.odooId}`);
  return parts.length > 0 ? parts.join(' | ') : (product._id || 'N/A');
}

const SECTION_KEYS = ['basicInfo', 'user', 'product', 'originalLinked', 'toLink', 'toDel', 'newLinked', 'error'];

function createSectionsState(value = false) {
  return SECTION_KEYS.reduce((acc, key) => { acc[key] = value; return acc; }, {});
}

export default function DataProductLinkedLog({ data, index }) {
  const [expandedSections, setExpandedSections] = useState(() => createSectionsState(false));
  const [isAllExpanded, setIsAllExpanded] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleAllSections = () => {
    const newState = !isAllExpanded;
    setIsAllExpanded(newState);
    setExpandedSections(createSectionsState(newState));
  };

  if (!data) {
    return (
      <div className="no-data-message">
        <p>No hay datos disponibles</p>
      </div>
    );
  }

  const hasError = !!(data.errorMsg || data.errorData);
  const isSync = data.method?.includes('linkProducts');
  const methodLabel = getMethodLabel(data.method);

  return (
    <div className="api-data-viewer w-100">
      <div className="record-header">
        <div className="record-header-content">
          <h2 className="record-title">
            <span className="record-index">#{index + 1}</span>
            {methodLabel}
          </h2>
          <div className="record-badges">
            <span className={`status-badge ${isSync ? 'status-active' : 'status-inactive'}`}>
              {isSync ? 'SYNC' : 'DESYNC'}
            </span>
            {data.massive && (
              <span className="progress-badge">MASIVO</span>
            )}
            {hasError && (
              <span className="status-badge status-inactive">ERROR</span>
            )}
          </div>
        </div>
        <button
          className="expand-all-btn"
          onClick={toggleAllSections}
          title={isAllExpanded ? 'Colapsar todo' : 'Expandir todo'}
        >
          {isAllExpanded ? '▼ Colapsar todo' : '▶ Expandir todo'}
        </button>
      </div>

      <CollapsibleSection
        title="Información Básica"
        isExpanded={expandedSections.basicInfo}
        onToggle={() => toggleSection('basicInfo')}
        icon="📋"
      >
        <div className="section-content">
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">ID:</span>
              <span className="detail-value detail-id">{data._id}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Método:</span>
              <span className="detail-value detail-code">{data.method || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Proceso:</span>
              <span className="detail-value">{data.processName || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Masivo:</span>
              <span className={`status-value ${data.massive ? 'active' : 'inactive'}`}>
                {data.massive ? 'Sí' : 'No'}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Creado:</span>
              <span className="detail-value detail-time">{formatDate(data.createdAt)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Actualizado:</span>
              <span className="detail-value detail-time">{formatDate(data.updatedAt)}</span>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Usuario"
        isExpanded={expandedSections.user}
        onToggle={() => toggleSection('user')}
        icon="👤"
        badge={getUserDisplay(data.requestedBy)}
        headerColor="#e2e2e242"
      >
        <div className="section-content">
          {data.requestedBy && typeof data.requestedBy === 'object' ? (
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">ID:</span>
                <span className="detail-value detail-id">{data.requestedBy._id}</span>
              </div>
              {data.requestedBy.name && (
                <div className="detail-item">
                  <span className="detail-label">Nombre:</span>
                  <span className="detail-value">{data.requestedBy.name}</span>
                </div>
              )}
              {data.requestedBy.email && (
                <div className="detail-item">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{data.requestedBy.email}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Referencia:</span>
                <span className="detail-value detail-id">
                  {data.requestedBy || 'Sistema / Automático'}
                </span>
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Producto Afectado"
        isExpanded={expandedSections.product}
        onToggle={() => toggleSection('product')}
        icon="📦"
        badge={getProductDisplay(data.productId)}
      >
        <div className="section-content">
          {data.productId && typeof data.productId === 'object' ? (
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">ID:</span>
                <span className="detail-value detail-id">{data.productId._id}</span>
              </div>
              {data.productId.name && (
                <div className="detail-item">
                  <span className="detail-label">Nombre:</span>
                  <span className="detail-value">{data.productId.name}</span>
                </div>
              )}
              {data.productId.sku && (
                <div className="detail-item">
                  <span className="detail-label">SKU:</span>
                  <span className="detail-value detail-code">{data.productId.sku}</span>
                </div>
              )}
              {data.productId.odooId && (
                <div className="detail-item">
                  <span className="detail-label">Odoo ID:</span>
                  <span className="detail-value">{data.productId.odooId}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Product ID:</span>
                <span className="detail-value detail-id">{data.productId || 'N/A'}</span>
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {data.originalLinked && data.originalLinked.length > 0 && (
        <CollapsibleSection
          title="Estado Anterior (originalLinked)"
          isExpanded={expandedSections.originalLinked}
          onToggle={() => toggleSection('originalLinked')}
          icon="📄"
          badge={`${data.originalLinked.length} elemento${data.originalLinked.length !== 1 ? 's' : ''}`}
          headerColor="#e2e2e242"
        >
          <div className="section-content">
            <JsonViewer data={data.originalLinked} />
          </div>
        </CollapsibleSection>
      )}

      {data.toLink && data.toLink.length > 0 && (
        <CollapsibleSection
          title="Productos a Vincular (toLink)"
          isExpanded={expandedSections.toLink}
          onToggle={() => toggleSection('toLink')}
          icon="🔗"
          badge={`${data.toLink.length} elemento${data.toLink.length !== 1 ? 's' : ''}`}
        >
          <div className="section-content">
            <JsonViewer data={data.toLink} />
          </div>
        </CollapsibleSection>
      )}

      {data.toDel && (
        <CollapsibleSection
          title="Producto a Desvincular (toDel)"
          isExpanded={expandedSections.toDel}
          onToggle={() => toggleSection('toDel')}
          icon="🔓"
          headerColor="#e2e2e242"
        >
          <div className="section-content">
            {typeof data.toDel === 'object' ? (
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">ID:</span>
                  <span className="detail-value detail-id">{data.toDel._id}</span>
                </div>
                {data.toDel.name && (
                  <div className="detail-item">
                    <span className="detail-label">Nombre:</span>
                    <span className="detail-value">{data.toDel.name}</span>
                  </div>
                )}
                {data.toDel.sku && (
                  <div className="detail-item">
                    <span className="detail-label">SKU:</span>
                    <span className="detail-value detail-code">{data.toDel.sku}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Product ID:</span>
                  <span className="detail-value detail-id">{data.toDel}</span>
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {data.newLinked && data.newLinked.length > 0 && (
        <CollapsibleSection
          title="Estado Final (newLinked)"
          isExpanded={expandedSections.newLinked}
          onToggle={() => toggleSection('newLinked')}
          icon="✅"
          badge={`${data.newLinked.length} elemento${data.newLinked.length !== 1 ? 's' : ''}`}
        >
          <div className="section-content">
            <JsonViewer data={data.newLinked} />
          </div>
        </CollapsibleSection>
      )}

      {hasError && (
        <CollapsibleSection
          title="Error"
          isExpanded={expandedSections.error}
          onToggle={() => toggleSection('error')}
          icon="⚠️"
          badge="ERROR"
          headerColor="#f8d7da"
        >
          <div className="section-content">
            {data.errorMsg && (
              <div style={{ marginBottom: '15px' }}>
                <div className="description-text" style={{ borderLeftColor: '#dc3545' }}>
                  {data.errorMsg}
                </div>
              </div>
            )}
            {data.errorData && (
              <JsonViewer data={data.errorData} title="Datos del error" />
            )}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}
